import { RolodexEntryJson } from 'types/rolodex/rolodexEntries'
import { parseArray, parseDate, parseString } from 'utils/parsingHelpers'

import { GeckoEntryBase } from './geckoEntry'

export enum GeckoFileCategory {
  GECKO_REPORT = 'gecko_report',
  NON_GECKO_REPORT = 'non_gecko_report',
  IMAGE = 'image',
  CONSTRUCTION_DRAWING = 'construction_drawing',
  INSPECTION_WALKDOWN_REPORT = 'inspection_walkdown_report',
  JSA = 'jsa',
  OEM_MANUAL = 'oem_manual',
  OTHER = 'other',
  UNKNOWN = 'unknown',
  OPERATING_MANUAL = 'operating_manual',
  INSTALLATION_MANUAL = 'installation_manual',
  MAINTENANCE_MANUAL = 'maintenance_manual',
  COMMISSIONING_MANUAL = 'commissioning_manual',
  INTEGRATED_VENDOR_MANUAL = 'integrated_vendor_manual',
  DATA_SHEET = 'data_sheet',
  SYSTEM_DESCRIPTION = 'system_description',
  BILL_OF_MATERIALS = 'bill_of_materials',
  INSTRUMENT_LIST = 'instrument_list',
  CONTROL_STRATEGIES = 'control_strategies',
  PROCEDURAL_DOCUMENT = 'procedural_document',
  OPERATING_PROCEDURES = 'operating_procedures',
  MAINTENANCE_PROCEDURES = 'maintenance_procedures',
  SAFETY_PROCEDURES = 'safety_procedures',
  PROJECT_REFERENCE_DOCUMENT = 'project_reference_document',
  VENDOR_PACKAGES = 'vendor_packages',
  DESIGN_CHANGE_NOTICE = 'design_change_notice',
  CERTIFICATIONS_TEST_REPORTS = 'certifications_test_reports',
  VIBRATION = 'vibration',
  OIL_ANALYSIS = 'oil_analysis',
  INSPECTION = 'inspection',
  PNID = 'pnid',
}

export const GeckoFileCategoryLabels = {
  [GeckoFileCategory.GECKO_REPORT]: 'Gecko Report',
  [GeckoFileCategory.NON_GECKO_REPORT]: 'Non-Gecko Report',
  [GeckoFileCategory.IMAGE]: 'Image',
  [GeckoFileCategory.CONSTRUCTION_DRAWING]: 'Construction Drawing',
  [GeckoFileCategory.OTHER]: 'Other',
  [GeckoFileCategory.INSPECTION_WALKDOWN_REPORT]: 'Inspection Walkdown Report',
  [GeckoFileCategory.JSA]: 'Job Safety Analysis',
  [GeckoFileCategory.OEM_MANUAL]: 'OEM Manual',
  [GeckoFileCategory.UNKNOWN]: 'Unknown',
  [GeckoFileCategory.OPERATING_MANUAL]: 'Operating Manual',
  [GeckoFileCategory.INSTALLATION_MANUAL]: 'Installation Manual',
  [GeckoFileCategory.MAINTENANCE_MANUAL]: 'Maintenance Manual',
  [GeckoFileCategory.COMMISSIONING_MANUAL]: 'Commissioning Manual',
  [GeckoFileCategory.INTEGRATED_VENDOR_MANUAL]: 'Integrated Vendor Manual',
  [GeckoFileCategory.DATA_SHEET]: 'Data Sheet',
  [GeckoFileCategory.SYSTEM_DESCRIPTION]: 'System Description',
  [GeckoFileCategory.BILL_OF_MATERIALS]:
    'Bill of Materials (BOM) / Major Equipment List',
  [GeckoFileCategory.INSTRUMENT_LIST]: 'Instrument List / I/O List',
  [GeckoFileCategory.CONTROL_STRATEGIES]: 'Control Strategies / Logic Diagram',
  [GeckoFileCategory.PROCEDURAL_DOCUMENT]: 'Procedural Document',
  [GeckoFileCategory.OPERATING_PROCEDURES]: 'Operating Procedures',
  [GeckoFileCategory.MAINTENANCE_PROCEDURES]:
    'Maintenance Procedures / Work Instructions',
  [GeckoFileCategory.SAFETY_PROCEDURES]: 'Safety Procedures',
  [GeckoFileCategory.PROJECT_REFERENCE_DOCUMENT]:
    'Project and Reference Document',
  [GeckoFileCategory.VENDOR_PACKAGES]: 'Vendor Packages',
  [GeckoFileCategory.DESIGN_CHANGE_NOTICE]:
    'Design Change Notice / As-Built Markup',
  [GeckoFileCategory.CERTIFICATIONS_TEST_REPORTS]:
    'Certifications / Test Reports',
  [GeckoFileCategory.VIBRATION]: 'Vibration',
  [GeckoFileCategory.OIL_ANALYSIS]: 'Oil Analysis',
  [GeckoFileCategory.INSPECTION]: 'Inspection',
  [GeckoFileCategory.PNID]: 'P&ID Diagram',
}

export const GeckoAllowableFileTypes = {
  [GeckoFileCategory.IMAGE]: 'image/*',
  [GeckoFileCategory.GECKO_REPORT]: 'application/pdf',
  [GeckoFileCategory.NON_GECKO_REPORT]: 'application/pdf, .doc, .docx, ',
  [GeckoFileCategory.CONSTRUCTION_DRAWING]: 'application/pdf',
  [GeckoFileCategory.INSPECTION_WALKDOWN_REPORT]: 'application/pdf',
  [GeckoFileCategory.JSA]: 'application/pdf',
  [GeckoFileCategory.OTHER]:
    'video/mp4, video/*, application/*, text/*, audio/*, !application/pdf',
  [GeckoFileCategory.OEM_MANUAL]: 'application/pdf,.doc,.docx',
  [GeckoFileCategory.UNKNOWN]: 'application/pdf,.doc,.docx',
  [GeckoFileCategory.OPERATING_MANUAL]: 'application/pdf,.doc,.docx',
  [GeckoFileCategory.INSTALLATION_MANUAL]: 'application/pdf,.doc,.docx',
  [GeckoFileCategory.MAINTENANCE_MANUAL]: 'application/pdf,.doc,.docx',
  [GeckoFileCategory.COMMISSIONING_MANUAL]: 'application/pdf,.doc,.docx',
  [GeckoFileCategory.INTEGRATED_VENDOR_MANUAL]: 'application/pdf,.doc,.docx',
  [GeckoFileCategory.DATA_SHEET]: 'application/pdf,.doc,.docx',
  [GeckoFileCategory.SYSTEM_DESCRIPTION]: 'application/pdf,.doc,.docx',
  [GeckoFileCategory.BILL_OF_MATERIALS]: 'application/pdf,.doc,.docx',
  [GeckoFileCategory.INSTRUMENT_LIST]: 'application/pdf,.doc,.docx',
  [GeckoFileCategory.CONTROL_STRATEGIES]: 'application/pdf,.doc,.docx',
  [GeckoFileCategory.PROCEDURAL_DOCUMENT]: 'application/pdf,.doc,.docx',
  [GeckoFileCategory.OPERATING_PROCEDURES]: 'application/pdf,.doc,.docx',
  [GeckoFileCategory.MAINTENANCE_PROCEDURES]: 'application/pdf,.doc,.docx',
  [GeckoFileCategory.SAFETY_PROCEDURES]: 'application/pdf,.doc,.docx',
  [GeckoFileCategory.PROJECT_REFERENCE_DOCUMENT]: 'application/pdf,.doc,.docx',
  [GeckoFileCategory.VENDOR_PACKAGES]: 'application/pdf,.doc,.docx',
  [GeckoFileCategory.DESIGN_CHANGE_NOTICE]: 'application/pdf,.doc,.docx',
  [GeckoFileCategory.CERTIFICATIONS_TEST_REPORTS]: 'application/pdf,.doc,.docx',
  [GeckoFileCategory.VIBRATION]: 'application/pdf,.doc,.docx',
  [GeckoFileCategory.OIL_ANALYSIS]: 'application/pdf,.doc,.docx',
  [GeckoFileCategory.INSPECTION]: 'application/pdf,.doc,.docx',
  [GeckoFileCategory.PNID]: 'application/pdf,.jpg,.jpeg,.png',
}

// https://developer.mozilla.org/en-US/docs/Web/Media/Formats/Image_types
export const IMAGE_TYPES = [
  'png',
  'jpg',
  'jpeg',
  'tiff',
  'jfif',
  'pjpeg',
  'pjp',
  'tif',
  'bmp',
  'gif',
  'svg',
  'webp',
]

export interface GeckoReportJSON {
  [key: string]: unknown
  visible_name: string
  file_caption?: string
  file_source?: string
  file_type: string | false
  upload_date: string
  document_date?: string
  size: number
  units: string[]
  components: string[]
  inspections: string[]
  jobs: string[]
  file_category: GeckoFileCategory
  viz_insp_field_responses?: string[]
  visual_inspections?: string[]
  description?: string
}

/**
 * An entry which contains metadata for a "report" file. In the
 * context could mean a PDF, PNG, JPEG, or any number of other formats.
 */
export class GeckoReport extends GeckoEntryBase<
  {
    name: string | undefined
    visibleName: string
    fileType: string
    jobs: ReadonlyArray<string> | undefined
    inspections: ReadonlyArray<string> | undefined
    units: ReadonlyArray<string> | undefined
    components: ReadonlyArray<string> | undefined
    upload_date: Date | undefined
    document_date: Date | undefined
    file_category: GeckoFileCategory | undefined
    file_caption: string | undefined
    file_source: string | undefined
    vizInspFieldResponses: ReadonlyArray<string> | undefined
    visualInspections: ReadonlyArray<string> | undefined
    pageRotations: Record<string, number> | undefined
    description: string | undefined
  },
  {
    organization: string
    site: string
    job?: string
    inspection?: string
    unit?: string
    component?: string
  }
> {
  //----------------------------------------------------------------------------

  /**
   * A simple constructor, private so that a more descriptive factory method
   * (which can return undefined) is used instead
   * @param json The raw JSON to create an instance from
   * @private
   */
  private constructor(json: RolodexEntryJson) {
    super(
      json,
      {
        name: parseString(json.data.name),
        visibleName: parseString(json.data.visible_name) ?? '',
        fileType: parseString(json.data.file_type) ?? '',
        jobs: parseArray<string>(json.data.jobs),
        inspections: parseArray<string>(json.data.inspections),
        units: parseArray<string>(json.data.units),
        components: parseArray<string>(json.data.components),
        upload_date: parseDate(json.data.upload_date),
        document_date: parseDate(json.data.document_date),
        file_category: IMAGE_TYPES.includes(
          parseString(json.data.file_type) ?? ''
        )
          ? GeckoFileCategory.IMAGE
          : ((parseString(json.data.file_category) as GeckoFileCategory) ??
            GeckoFileCategory.OTHER),
        file_caption: parseString(json.data.file_caption),
        file_source: parseString(json.data.file_source),
        description: parseString(json.data.description),
        vizInspFieldResponses: parseArray<string>(
          json.data.viz_insp_field_responses
        ),
        visualInspections: parseArray<string>(json.data.visual_inspections),
        pageRotations:
          json.data.pageRotations &&
          typeof json.data.pageRotations === 'object' &&
          !Array.isArray(json.data.pageRotations)
            ? (json.data.pageRotations as Record<string, number>)
            : undefined,
      },
      {
        organization: parseString(json.tags.organization) ?? '',
        site: parseString(json.tags.site) ?? '',
        inspection: parseString(json.tags.unit),
        job: parseString(json.tags.unit),
        component: parseString(json.tags.unit),
        unit: parseString(json.tags.unit),
      },
      {}
    )
  }

  //----------------------------------------------------------------------------

  /**
   * A simple factory method which creates a report from raw JSON
   * @param json The raw JSON to create an instance from
   * @constructor
   */
  public static CreateFromJSON(json: RolodexEntryJson) {
    return new GeckoReport(json)
  }
}
